import React, { useEffect, useState } from 'react';
import sizeMe, { SizeMeProps } from 'react-sizeme';
import CodeBlockToolbar from './CodeBlockToolbar';
import CodeBlockInput from './CodeBlockInput';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Snackbar,
  TextField,
} from '@mui/material';
import { documentService } from '../../services/DocumentService';
import { CodeBlock, DocumentNode } from '../../types/RealtaDocument';
import { useLocation } from '@gatsbyjs/reach-router';
import useRole from '../../utils/use-role';
import useUsername from '../../utils/use-username';
import _ from 'lodash';

type CodeBlockGridProps = {
  size: SizeMeProps['size'];
  node: DocumentNode;
  shortName?: string;
  onChangeAlert: (value: boolean) => void;
};

const CodeBlockGrid: React.FC<CodeBlockGridProps> = ({ node, shortName, onChangeAlert }) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const documentId = queryParams.get('document-id') || '';
  const { isReadOnly } = useRole();
  const username = useUsername();

  const [selectedCodeBlock, setSelectedCodeBlock] = useState<CodeBlock | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hightLight, setHightLight] = useState<boolean>(true);
  const [lineWrapping, setLineWrapping] = useState<boolean>(true);
  const [newDocumentShortName, setNewDocumentShortName] = useState<string>(shortName || '');
  const [openDialog, setOpenDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [open, setOpen] = React.useState(false);
  const [rule, setRule] = React.useState('');

  useEffect(() => {
    const getCodeBlock = async () => {
      setIsLoading(true);
      try {
        const codeblock = await documentService.getCodeBlockByNodeId({ parentNodeUuid: node.uuid });
        setSelectedCodeBlock(codeblock);
        setRule(codeblock?.rule || '');
      } catch (error) {
        console.error(error);
      }
      setIsLoading(false);
    };

    getCodeBlock();
  }, [node]);

  useEffect(() => {
    const handleBeforeUnload = (event: any) => {
      if (rule && rule !== selectedCodeBlock?.rule) {
        // Custom message for older browsers
        const message = 'Changes you made may not be saved.';
        event.returnValue = message; // Standard property for modern browsers
        return message; // Required for older browsers
      }
      return null;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [selectedCodeBlock, rule]);

  const createCodeBlock = async () => {
    try {
      setIsLoading(true);
      const codeBlock = await documentService.createCodeBlock({
        rule: '',
        parentNode: node.uuid,
        createdBy: username,
      });
      if (codeBlock) {
        setSelectedCodeBlock(codeBlock);
      }
    } catch (error) {
      console.error(error);
    }
    setIsLoading(false);
  };

  const handleRuleChange = (value: string) => {
    onChangeAlert(value !== selectedCodeBlock?.rule);
    setRule(value);
  };

  const generateCodeBlock = async () => {
    if (!newDocumentShortName) {
      setOpenDialog(true);
      return;
    }
    try {
      setIsLoading(true);
      if (selectedCodeBlock) {
        const newRule = await documentService.generateCodeBlockData({
          documentShortName: newDocumentShortName,
          documentUuid: documentId,
          uuid: node.uuid,
        });
        setRule(newRule);
        if (newRule !== selectedCodeBlock.rule) {
          onChangeAlert(true);
        }
      } else {
        const codeBlock = await documentService.createCodeBlock({
          rule: await documentService.generateCodeBlockData({
            documentShortName: newDocumentShortName,
            documentUuid: documentId,
            uuid: node.uuid,
          }),
          parentNode: node.uuid,
          createdBy: username,
        });
        if (codeBlock) {
          setSelectedCodeBlock(codeBlock);
          setRule(codeBlock.rule || '');
        }
      }
    } catch (error) {
      console.error(error);
    }
    setIsLoading(false);
  };

  const saveCodeBlock = async () => {
    if (!selectedCodeBlock) return;
    const value = rule;
    const previousValue = selectedCodeBlock?.rule || '';

    const isDeletion = value.length < previousValue.length;
    if (isDeletion) {
      const minLength = Math.min(value.length, previousValue.length);
      let diffIndex = minLength;
      for (let i = 0; i < minLength; i++) {
        if (value[i] !== previousValue[i]) {
          diffIndex = i;
          break;
        }
      }
      const deletedText = previousValue.slice(diffIndex);
      documentService.deleteAtom(deletedText);
    } else {
      documentService.createAtoms({ content: value, parentNode: selectedCodeBlock.uuid });
    }
    const newCodeBlock = await documentService.updateCodeBlock({
      uuid: selectedCodeBlock.uuid,
      rule: value,
      updatedBy: username,
    });
    setSelectedCodeBlock(newCodeBlock);
    onChangeAlert(false);

    setOpen(true);
  };

  const handleAddShortNameAndGenerateCodeBlock = async () => {
    try {
      if (!newDocumentShortName) {
        setErrorMessage('Document Short Name is required');
        return;
      }
      await documentService.updateDocument({ uuid: documentId, shortName: newDocumentShortName });
      await generateCodeBlock();
    } catch (error) {
      console.error(error);
    }

    setOpenDialog(false);
  };

  const handleClose = (_: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpen(false);
  };

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  return (
    <>
      <Snackbar open={open} autoHideDuration={3000} onClose={handleClose} message="Code Block is updated" />
      {selectedCodeBlock ? (
        <Box boxShadow={5} height="100%" display="flex" flexDirection="column" p={1}>
          <CodeBlockToolbar
            codeBlock={selectedCodeBlock}
            hightLight={hightLight}
            lineWrapping={lineWrapping}
            setHightLight={setHightLight}
            setLineWrapping={setLineWrapping}
            generateCodeBlock={generateCodeBlock}
            saveCodeBlock={saveCodeBlock}
          />
          <CodeBlockInput
            rule={rule}
            hightLight={hightLight}
            lineWrapping={lineWrapping}
            readOnly={isReadOnly}
            onRuleChange={handleRuleChange}
          />
        </Box>
      ) : (
        <Box display="flex" alignItems="center" justifyContent="center" height="100%">
          <Box display="flex" gap={2}>
            <Button variant="contained" color="secondary" onClick={createCodeBlock} disabled={isReadOnly}>
              CREATE CODE BLOCK
            </Button>
            <Button variant="contained" color="secondary" onClick={() => generateCodeBlock()} disabled={isReadOnly}>
              GENERATE CODE BLOCK
            </Button>
          </Box>
        </Box>
      )}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth>
        <DialogTitle>Add Document Short Name</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Document Short Name"
            type="text"
            fullWidth
            value={newDocumentShortName}
            color="secondary"
            onChange={(e) => {
              setErrorMessage('');
              setNewDocumentShortName(e.target.value);
            }}
            disabled={isReadOnly}
            error={!!errorMessage}
          />
          {errorMessage && <FormHelperText error>{errorMessage}</FormHelperText>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button color="secondary" onClick={handleAddShortNameAndGenerateCodeBlock} disabled={isReadOnly}>
            Generate
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default sizeMe({
  monitorHeight: false,
  monitorWidth: true,
  noPlaceholder: true,
})(CodeBlockGrid);
